import { sendADT1Email } from '@commenda/api';
import { Button, Icons } from '@commenda/kakashi';
import {
  TaskStatus,
  TaskParty,
} from '@prisma/client';
import { useState } from 'react';
import { useNotification } from '../../../../hooks/useNotification';
import { ServiceRequestTaskLabel } from '../../../common/labels';
import { ServiceRequestTaskIconConfig } from '../../../common/taskIconConfig';
import { ServiceRequestTaskCard } from '../../../layout/components/taskCard/ServiceRequestTaskCard';
import { ServiceRequestTaskCardConfig, TCommonTaskProp } from '../../../types';
import { updateTaskStatus } from '../../../utils';

type ADT1FilingHandoffTaskMetadata = {
  handoffAction?: 'neeti' | 'upsell';
};

export const ADT1FilingHandoffTask = ({
  task,
  context,
  isTimelineView = false,
  displayNumber,
}: TCommonTaskProp) => {
  const { setNotification } = useNotification();
  const { role, serviceRequest, mutate } = context;
  const getHandoffAction = (): 'neeti' | 'upsell' | null => {
    if (task.metadata && typeof task.metadata === 'object') {
      return (
        (task.metadata as ADT1FilingHandoffTaskMetadata)?.handoffAction || null
      );
    }
    return null;
  };

  const [handoffAction, setHandoffAction] = useState<'neeti' | 'upsell' | null>(
    getHandoffAction()
  );

  // Hide this task from clients - it's an internal agent task
  if (role === TaskParty.CLIENT) {
    return null;
  }

  const handleHandoff = async () => {
    setHandoffAction('neeti');
    const companyName = serviceRequest?.corporation?.companyLegalName;

    try {
      await sendADT1Email({ companyName });

      await updateTaskStatus({
        task,
        status: TaskStatus.COMPLETED,
        mutate,
        metadata: { handoffAction: 'neeti' },
      });
      setNotification({
        show: true,
        valid: true,
        title: 'Success',
        message: 'ADT-1 email sent',
      });
    } catch (error) {
      console.error('Failed to send ADT-1 email:', error);
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: 'Failed to send ADT-1 email',
      });
    }
  };

  const handleFillUpsellForm = async () => {
    setHandoffAction('upsell');
    await updateTaskStatus({
      task,
      status: TaskStatus.COMPLETED,
      mutate,
      metadata: { handoffAction: 'upsell' },
    });
  };

  const openUpsellForm = () => {
    window.open(
      'https://docs.google.com/forms/d/e/1FAIpQLSfWFq0rfZoE_CWsTUlluZrXrWkYrumB4jh9I-QeQ0QVFrieuQ/viewform',
      '_blank'
    );
  };

  const config: ServiceRequestTaskCardConfig = {
    getLeftIcon: (status) => ServiceRequestTaskIconConfig[status],
    getStateConfig: (state) => {
      const stateMap = {
        [TaskParty.AGENT]: {
          [TaskStatus.TO_DO]: {
            actions: (
              <div className="flex flex-col gap-2">
                <div className="text-sm text-gray-600 mb-2">
                  Check Chargebee to see if customer paid for compliance:
                </div>
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleHandoff} variant="primary">
                    <Icons.Check className="size-4 mr-2" />
                    Paid for Compliance - Handoff to Neeti
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() => {
                      handleFillUpsellForm();
                      openUpsellForm();
                    }}
                  >
                    <Icons.File01 className="size-4 mr-2" />
                    Not Paid - Fill Upsell Form
                  </Button>
                </div>
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel label="Check Chargebee and handle ADT-1 filing" />
            ),
          },
          [TaskStatus.BACKLOG]: {
            actions: (
              <div className="flex flex-col gap-2">
                <div className="text-sm text-gray-600 mb-2">
                  Check Chargebee to see if customer paid for compliance:
                </div>
                <div className="flex gap-2">
                  <Button size="sm" disabled variant="primary">
                    <Icons.Check className="size-4 mr-2" />
                    Paid for Compliance - Handoff to Neeti
                  </Button>
                  <Button size="sm" variant="secondary" disabled>
                    <Icons.File01 className="size-4 mr-2" />
                    Not Paid - Fill Upsell Form
                  </Button>
                </div>
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel label="Check Chargebee and handle ADT-1 filing" />
            ),
          },
          [TaskStatus.COMPLETED]: {
            actions: (
              <div className="flex flex-col gap-2">
                <div className="text-sm text-gray-600">
                  {handoffAction === 'neeti' ? (
                    <span className="text-primary">
                      Handed off to Neeti for compliance onboarding
                    </span>
                  ) : handoffAction === 'upsell' ? (
                    <span className="text-primary">Upsell form completed</span>
                  ) : (
                    <span className="text-primary">Task completed</span>
                  )}
                </div>
                {handoffAction === 'upsell' && (
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={openUpsellForm}
                  >
                    <Icons.ArrowUpRight className="size-4 mr-2" />
                    Open Upsell Form
                  </Button>
                )}
              </div>
            ),
            label: (
              <ServiceRequestTaskLabel
                label={
                  handoffAction === 'neeti'
                    ? 'ADT-1 filing handed off to compliance team'
                    : handoffAction === 'upsell'
                      ? 'Upsell form completed'
                      : 'Task completed'
                }
                status={task.status}
                timestamp={task.updatedAt}
              />
            ),
          },
        },
      };

      return (
        // Admin viewers run these steps too, and an unmapped role renders no actions.
        stateMap[state.role === TaskParty.ADMIN ? TaskParty.AGENT : state.role]?.[
          state.status
        ] ?? {
          label: task.title,
          actions: null,
        }
      );
    },
  };

  return (
    <div>
      <ServiceRequestTaskCard
        task={task}
        state={{ role, status: task.status }}
        config={config}
        context={context}
        isTimelineView={isTimelineView}
        displayNumber={displayNumber}
      />
    </div>
  );
};
